<!-- BottomMenu.vue -->
<template>
  <div v-if="user && userData?.characterCreated">
    <!-- Modal Overlays -->
    <transition name="fade">
      <div v-if="toggleModal === 'AdventureModal'" class="fixed inset-0 flex items-center justify-center z-50">
        <div class="fixed inset-0 bg-gray-900 bg-opacity-80" @click="toggleModal = ''"></div>
        <AdventureModal @cancelModal="toggleModal = ''" @exploreMap="handleExploreMap" />
      </div>
    </transition>
    <transition name="fade">
      <div v-if="toggleModal === 'MoveRoomModal'" class="fixed inset-0 flex items-center justify-center z-50">
        <div class="fixed inset-0 bg-gray-900 bg-opacity-80" @click="toggleModal = ''"></div>
        <MoveRoomModal :is-open="toggleModal === 'MoveRoomModal'" @close="toggleModal = ''" />
      </div>
    </transition>
    <transition name="fade">
      <div v-if="toggleModal === 'BagModal'" class="fixed inset-0 flex items-center justify-center z-50">
        <div class="fixed inset-0 bg-gray-900 bg-opacity-80" @click="toggleModal = ''"></div>
        <BagModal @closeInventory="toggleModal = ''" />
      </div>
    </transition>
    <transition name="fade">
      <div v-if="toggleModal === 'StatsModal'" class="fixed inset-0 flex items-center justify-center z-50">
        <div class="fixed inset-0 bg-gray-900 bg-opacity-80" @click="toggleModal = ''"></div>
        <StatsModal @cancelModal="toggleModal = ''" />
      </div>
    </transition>
    <transition name="fade">
      <div v-if="toggleModal === 'EquippedItemsModal'" class="fixed inset-0 flex items-center justify-center z-50">
        <div class="fixed inset-0 bg-gray-900 bg-opacity-80" @click="toggleModal = ''"></div>
        <EquippedItemsModal @closeEquippedItems="toggleModal = ''" />
      </div>
    </transition>
    <transition name="fade">
      <div v-if="toggleModal === 'MissionModal'" class="fixed inset-0 flex items-center justify-center z-50">
        <div class="fixed inset-0 bg-gray-900 bg-opacity-80" @click="toggleModal = ''"></div>
        <MissionModal
          :missions="missions"
          :daily-missions="dailyMissions"
          :weekly-missions="weeklyMissions"
          :character-stats="characterStats"
          :daily-stats="dailyCharacterStats"
          :weekly-stats="weeklyCharacterStats"
          :completed-missions="completedMissions"
          :claimed-daily-missions="claimedDailyMissions"
          :claimed-weekly-missions="claimedWeeklyMissions"
          @cancelModal="toggleModal = ''"
        />
      </div>
    </transition>

    <!-- Bottom Navigation -->
    <nav class="fixed inset-x-0 max-w-screen-xl mx-auto bottom-0 z-40 bg-gray-900 text-white border-t border-gray-700" style="padding-bottom: env(safe-area-inset-bottom);">
      <div class="grid grid-cols-6 gap-1 p-1">
        <!-- Bag -->
        <button @click="toggleModal = 'BagModal'" class="flex flex-col items-center justify-center py-2 rounded-lg bg-gray-900 hover:bg-gray-800 transition-all duration-200" :class="{ 'border-2 border-indigo-500': toggleModal === 'BagModal' }">
          <i class="fas fa-suitcase-rolling text-xs sm:text-base mb-1" :class="bagWeightColor"></i>
          <span class="text-xs sm:text-sm font-semibold" :class="bagWeightColor">กระเป๋า</span>
        </button>
        <!-- Equipped Items -->
        <button @click="toggleModal = 'EquippedItemsModal'" class="flex flex-col items-center justify-center py-2 rounded-lg bg-gray-900 hover:bg-gray-800 transition-all duration-200" :class="{ 'border-2 border-indigo-500': toggleModal === 'EquippedItemsModal' }">
          <i class="fas fa-shirt text-xs sm:text-base mb-1 text-white"></i>
          <span class="text-xs sm:text-sm font-semibold text-white">อุปกรณ์</span>
        </button>
        <!-- Adventure -->
        <div class="col-span-2 grid grid-cols-3 gap-1">
          <button @click="toggleModal = 'AdventureModal'" class="col-span-2 flex flex-col items-center justify-center py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 transition-all duration-200 border border-indigo-700" :class="{ 'bg-indigo-700': toggleModal === 'AdventureModal' }">
            <i class="fas fa-hiking text-xs sm:text-base mb-1 text-white"></i>
            <span class="text-xs sm:text-sm font-semibold text-white">สำรวจ</span>
          </button>
          <button
            @click="quickExplore"
            :disabled="!quickExploreStatus.canExplore || isQuickExploring"
            :title="quickExploreStatus.canExplore ? `สำรวจ ${lastExploreContext?.mapName || 'ล่าสุด'} ทันที` : quickExploreStatus.reason"
            class="col-span-1 flex flex-col items-center justify-center py-2 rounded-lg transition-all duration-200 border text-white"
            :class="[
              quickExploreStatus.canExplore && !isQuickExploring
                ? 'bg-emerald-600 hover:bg-emerald-500 border-emerald-700'
                : 'bg-gray-700 border-gray-600 opacity-70 cursor-not-allowed'
            ]"
          >
            <i v-if="!isQuickExploring" class="fas fa-redo text-xs sm:text-base mb-1"></i>
            <div v-else class="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-t-2 border-white mb-1"></div>
            <span class="text-xxs sm:text-xs font-semibold truncate max-w-full px-1">
              {{ isQuickExploring ? 'กำลัง...' : (lastExploreContext?.mapName || 'ล่าสุด') }}
            </span>
          </button>
        </div>
        <!-- Stats -->
        <button v-if="characterData && characterData.level < 30" @click="toggleModal = 'StatsModal'" class="relative flex flex-col items-center justify-center py-2 rounded-lg bg-gray-900 hover:bg-gray-800 transition-all duration-200" :class="{ 'border-2 border-indigo-500': toggleModal === 'StatsModal' }">
          <i class="fas fa-chart-bar text-xs sm:text-base mb-1 text-white"></i>
          <span class="text-xs sm:text-sm font-semibold text-white flex items-center">
            สเตตัส
            <span v-if="characterPrivateData?.statPoints > 0" class="bg-gradient-to-r from-red-600 to-pink-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center ml-1 border border-red-400 animate-pulse-glow">
              {{ characterPrivateData.statPoints }}
            </span>
          </span>
        </button>
        <!-- Move Room -->
        <button v-if="characterData && characterData.level >= 30" @click="toggleModal = 'MoveRoomModal'" class="flex flex-col items-center justify-center py-2 rounded-lg bg-gray-900 hover:bg-gray-800 transition-all duration-200" :class="{ 'border-2 border-indigo-500': toggleModal === 'MoveRoomModal' }">
          <i class="fas fa-person-booth text-xs sm:text-base mb-1 text-white"></i>
          <span class="text-xs sm:text-sm font-semibold text-white">เปลี่ยนห้อง</span>
        </button>
        <!-- Mission -->
        <button @click="openMissionModal" class="relative flex flex-col items-center justify-center py-2 rounded-lg bg-gray-900 hover:bg-gray-800 transition-all duration-200" :class="{ 'border-2 border-indigo-500': toggleModal === 'MissionModal' }">
          <i class="fas fa-tasks text-xs sm:text-base mb-1" :class="hasIncompleteMissions && claimableMissionCount === 0 ? 'text-red-500' : 'text-white'"></i>
          <span class="text-xs sm:text-sm font-semibold flex items-center" :class="hasIncompleteMissions && claimableMissionCount === 0 ? 'text-red-500' : 'text-white'">
            ภารกิจ
            <span v-if="claimableMissionCount > 0" class="bg-gradient-to-r from-red-600 to-pink-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center ml-1 border border-red-400 animate-pulse-glow">
              {{ claimableMissionCount }}
            </span>
            <span v-else-if="hasIncompleteMissions" class="bg-gradient-to-r from-red-600 to-pink-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center ml-1 border border-red-400 animate-pulse-glow">
              <i class="fas fa-exclamation text-[8px]"></i>
            </span>
          </span>
        </button>
      </div>
    </nav>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { useInventoryStore } from '@/composables/useInventoryStore';
import { useMissionStatsStore } from '@/composables/useMissionStatsStore';
import { useQuickExplore, canQuickExplore, getLastExploreContext } from '@/composables/useQuickExplore';
import AdventureModal from '@/components/character/AdventureModal.vue';
import MoveRoomModal from '@/components/character/MoveRoomModal.vue';
import BagModal from '@/components/character/BagModal.vue';
import StatsModal from '@/components/character/StatsModal.vue';
import EquippedItemsModal from '@/components/character/EquippedItemsModal.vue';
import MissionModal from '@/components/character/MissionModal.vue';
import missionsData from '@/data/missions.json';

export default {
  name: 'BottomMenu',
  components: { AdventureModal, MoveRoomModal, BagModal, StatsModal, EquippedItemsModal, MissionModal },
  props: {
    characterPrivateData: { type: Object, default: null },
    userData: { type: Object, default: null },
    characterData: { type: Object, default: null },
  },
  setup(props) {
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const inventoryStore = useInventoryStore();
    const missionStatsStore = useMissionStatsStore();
    const { explore, isExploring: isQuickExploring } = useQuickExplore();
    const toggleModal = ref('');
    const lastExploreContext = ref(getLastExploreContext());
    const completedMissions = ref([]);
    const claimedDailyMissions = ref([]);
    const claimedWeeklyMissions = ref([]);
    const bagData = ref({ currentWeight: 0, maxLoad: 100 });

    const missions = ref(missionsData.main || []);
    const dailyMissions = ref(missionsData.daily || []);
    const weeklyMissions = ref(missionsData.weekly || []);

    watch(() => inventoryStore.bagData, (data) => {
      bagData.value = data || { currentWeight: 0, maxLoad: 100 };
    }, { immediate: true });

    watch(toggleModal, (newVal, oldVal) => {
      if (oldVal === 'AdventureModal' && newVal === '') {
        lastExploreContext.value = getLastExploreContext();
      }
    });

    watch(() => playerStore.characterPrivateData, (data) => {
      completedMissions.value = data?.completedMissions || [];
      claimedDailyMissions.value = data?.claimedDailyMissions || [];
      claimedWeeklyMissions.value = data?.claimedWeeklyMissions || [];
    }, { immediate: true });

    // Calculate bag weight percentage
    const bagWeightPercentage = computed(() => {
      const { currentWeight, maxLoad } = bagData.value;
      return maxLoad > 0 ? (currentWeight / maxLoad) * 100 : 0;
    });

    // Determine color class based on weight percentage
    const bagWeightColor = computed(() => {
      const percentage = bagWeightPercentage.value;
      if (percentage < 30) return 'text-green-500';
      if (percentage < 80) return 'text-yellow-500';
      return 'text-red-500';
    });

    // Compute incomplete missions (main)
    const incompleteMainMissions = computed(() => {
      if (!user.value) return [];
      return missions.value
        .filter(mission => mission.active && (mission.repeatable || !completedMissions.value.includes(mission.missionId)))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    // Compute incomplete daily missions
    const incompleteDailyMissions = computed(() => {
      if (!user.value) return [];
      return dailyMissions.value
        .filter(mission => mission.active && !claimedDailyMissions.value.includes(mission.missionId))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    // Compute incomplete weekly missions
    const incompleteWeeklyMissions = computed(() => {
      if (!user.value) return [];
      return weeklyMissions.value
        .filter(mission => mission.active && !claimedWeeklyMissions.value.includes(mission.missionId))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    // Check if there are incomplete missions (total)
    const hasIncompleteMissions = computed(() => 
      incompleteMainMissions.value.length > 0 ||
      incompleteDailyMissions.value.length > 0 ||
      incompleteWeeklyMissions.value.length > 0
    );

    // Count claimable missions (total)
    const claimableMissionCount = computed(() => {
      if (!user.value) return 0;
      let count = 0;

      const roomLevel = props.characterData?.room || 0;
      const cs = missionStatsStore.characterStatsData || {};
      const dcs = missionStatsStore.dailyCharacterStatsData || {};
      const wcs = missionStatsStore.weeklyCharacterStatsData || {};

      // Main missions (always count if claimable)
      count += incompleteMainMissions.value.reduce((acc, mission) => {
        let currentValue = getMissionValue(mission, cs, props.characterData);
        return currentValue >= (mission.targetValue || 0) ? acc + 1 : acc;
      }, 0);

      // Daily missions (count only if room >= 5)
      count += incompleteDailyMissions.value.reduce((acc, mission) => {
        let currentValue = getMissionValue(mission, dcs, props.characterData);
        return (currentValue >= (mission.targetValue || 0) && roomLevel >= 5) ? acc + 1 : acc;
      }, 0);

      // Weekly missions (count only if room >= 5)
      count += incompleteWeeklyMissions.value.reduce((acc, mission) => {
        let currentValue = getMissionValue(mission, wcs, props.characterData);
        return (currentValue >= (mission.targetValue || 0) && roomLevel >= 5) ? acc + 1 : acc;
      }, 0);

      return count;
    });

    // Helper to get mission value based on type
    const getMissionValue = (mission, stats, characterData) => {
      let currentValue = 0;
      switch (mission.type) {
        case 'explore_specific_map': currentValue = stats.explorationsByMap?.[mission.mapName] || 0; break;
        case 'zombiesKilled': currentValue = stats.zombiesKilled || 0; break;
        case 'zombiesKilled_specific': currentValue = stats.zombiesKilledByName?.[mission.zombieName] || 0; break;
        case 'criminalsKilled_specific': currentValue = stats.criminalsKilledByName?.[mission.criminalName] || 0; break;
        case 'decomposedItems': currentValue = stats.decomposedItems || 0; break;
        case 'decomposedItemsByName': currentValue = stats.decomposedItemsByName?.[mission.itemName] || 0; break;
        case 'skillsLearned': currentValue = stats.totalSkillsLearned || 0; break;
        case 'tattoosApplied': currentValue = stats.tattoosApplied || 0; break;
        case 'mapsExplored': currentValue = stats.mapsExplored || 0; break;
        case 'roomUpgrades': currentValue = stats.roomUpgrades || 0; break;
        case 'criminalsKilled': currentValue = stats.criminalsKilled || 0; break;
        case 'survivorsKilled': currentValue = stats.survivorsKilled || 0; break;
        case 'craftedItems': currentValue = stats.craftedItems || 0; break;
        case 'craftedItemsByName': currentValue = stats.craftedItemsByName?.[mission.itemName] || 0; break;
        case 'totalSilverFromItemSales':
        case 'silverFromItemSales': currentValue = stats.silverFromItemSales || 0; break;
        case 'equipmentUpgrades': currentValue = stats.equipmentUpgrades || 0; break;
        case 'mechanicalUpgrades': currentValue = stats.mechanicalUpgrades || 0; break;
        case 'level': currentValue = characterData?.level || 1; break;
        case 'raidBossParticipations' : currentValue = stats.raidBossParticipations || 0; break;
        default: break;
      }
      return currentValue;
    };

    const openMissionModal = async () => {
      await missionStatsStore.fetch(user.value?.uid);
      toggleModal.value = 'MissionModal';
    };

    const quickExploreStatus = computed(() => {
      return canQuickExplore(lastExploreContext.value, props.characterPrivateData);
    });

    const quickExplore = async () => {
      if (!lastExploreContext.value || !quickExploreStatus.value.canExplore) return;

      try {
        await explore(lastExploreContext.value.mapName, {
          mapType: lastExploreContext.value.mapType,
          usedVehicle: lastExploreContext.value.usedVehicle
        });
      } catch (err) {
        console.error('Quick explore failed:', err);
        window.alert(err.message || 'สำรวจล้มเหลว');
      }
    };

    const handleExploreMap = (map) => {
      console.log('สำรวจแผนที่:', map);
      toggleModal.value = '';
    };

    return {
      user,
      toggleModal,
      missions,
      dailyMissions,
      weeklyMissions,
      handleExploreMap,
      openMissionModal,
      hasIncompleteMissions,
      claimableMissionCount,
      characterStats: computed(() => missionStatsStore.characterStatsData || {}),
      dailyCharacterStats: computed(() => missionStatsStore.dailyCharacterStatsData || {}),
      weeklyCharacterStats: computed(() => missionStatsStore.weeklyCharacterStatsData || {}),
      completedMissions,
      claimedDailyMissions,
      claimedWeeklyMissions,
      bagWeightColor,
      lastExploreContext,
      quickExploreStatus,
      quickExplore,
      isQuickExploring,
    };
  },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
@keyframes pulse-glow {
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
  50% { transform: scale(1.1); box-shadow: 0 0 8px 2px rgba(239, 68, 68, 0.5); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
}
.animate-pulse-glow { animation: pulse-glow 1.5s infinite ease-in-out; }
</style>
